import { get } from './client'

/**
 * The directory, and one partner's page.
 *
 * The two return deliberately different shapes, and it is worth knowing which before
 * building anything on them:
 *
 *   - **the list** is the browsing path. It carries the profile only — no pricing, no
 *     open-now — because reading settings and the roster per row would turn a directory
 *     page into three database reads per partner.
 *   - **the detail** is where the Order button sits, so it carries everything that button
 *     depends on: `pricing`, `isOpenNow`, `closedReason`, `prepTimeMinutes`,
 *     `serviceArea` and `hours`.
 *
 * Neither carries `contactNumber`. The API withholds it from customers on purpose — it is
 * on the partner's Facebook page anyway, and serving it from a paginated endpoint would
 * let anyone collect every partner's number in a few requests. Admins and the partner
 * themselves still see it.
 */

/**
 * `GET /api/partners`
 *
 * `search` is a real server-side parameter, but not a database query: Firestore cannot
 * run a substring match across three fields, so the server scans a bounded window of
 * active partners and filters there. It matches business name, city and barangay.
 *
 * A capped scan comes back as `pagination.capped`, which is the server saying "there may
 * be more than this" rather than pretending the list is complete.
 *
 * Only active partners are ever returned — a paused partner is absent, not flagged.
 */
export async function getPartners({ search = '', page = 1, limit } = {}) {
  const params = new URLSearchParams()

  if (search.trim()) params.set('search', search.trim())
  if (page) params.set('page', String(page))
  if (limit) params.set('limit', String(limit))

  return get(`/api/partners?${params}`)
}

/**
 * `GET /api/partners/:slug`
 *
 * A paused partner reads as **404, not "closed"** — the same as a slug that never
 * existed. That is deliberate: a link somebody kept from a partner who has since been
 * suspended should not confirm that the partner exists.
 */
export const getPartner = (slug) => get(`/api/partners/${encodeURIComponent(slug)}`)
